import { useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId, TaskId } from 'api/models';
import { Action, EventType, ObjectType } from 'api/pushes';
import { Task, TaskAPI } from 'api/tasks';
import { Actor, ActorName, FetchStatus } from 'data/core';
import { deleteTask, saveTasks } from 'data/tasks/slice';
import { useTaskIdDebouncer } from 'hooks/use-task-id-debouncer';
import { getProjectTasksQueryKey } from 'queries/common';
import { withValidCustomFieldsForData } from 'utils/duplicate-fields-transformer';
import { getHopId, safelyParseJSON } from 'utils/window-utils';
import { debouncedFetchTaskSaga } from '../use-debounced-fetch-task';
import { useProjectWSUpdates } from '../use-project-ws-updates';

export const useProjectTaskWebsocket = (
  [, projectId]: ProjectId,
  options?: {
    onFetchTaskSuccess?: (task: Task | undefined) => void;
    actorName?: ActorName;
    skipStoreUpdate?: boolean;
  },
): void => {
  const hopId = getHopId();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const taskIdDebouncer = useTaskIdDebouncer(debouncedFetchTaskSaga, 100);
  const handleTaskSocketUpdate = useCallback(
    async (socketData) => {
      const { hopId: eventHopId, actor, action, data } = socketData;
      const { actorName, actorId } = safelyParseJSON(actor) ?? ({} as Actor);
      const allowUpdate =
        action !== Action.Update ||
        actorName === undefined ||
        eventHopId === undefined ||
        options?.actorName !== actorName ||
        data.taskId !== actorId ||
        eventHopId !== hopId;

      switch (action) {
        case Action.Duplicate:
          socketData.data.forEach(async (item) => {
            const task = await TaskAPI.getTask(socketData.projectId, item);
            const updatedTask = {
              ...task,
              fields: task.fields.map(withValidCustomFieldsForData),
            };

            if (options?.skipStoreUpdate) {
              queryClient.setQueryData<Task[]>(
                getProjectTasksQueryKey(ProjectId(Number(projectId))),
                (tasks) => {
                  return tasks?.concat([updatedTask]) ?? [];
                },
              );
            } else {
              dispatch(
                saveTasks({
                  data: [updatedTask],
                  fetchStatus: FetchStatus.Loaded,
                }),
              );
            }
          });

          break;
        case Action.Create:
        case Action.Update:
          if (allowUpdate) {
            taskIdDebouncer(
              TaskId(socketData.projectId, data.taskId),
              dispatch,
              options?.onFetchTaskSuccess,
              options?.skipStoreUpdate,
            );
          }
          break;

        case Action.Delete:
          if (options?.skipStoreUpdate) {
            queryClient.setQueryData<Task[]>(
              getProjectTasksQueryKey(ProjectId(Number(projectId))),
              (tasks) => {
                return (
                  tasks?.filter((task) => task.taskId !== data.taskId) ?? []
                );
              },
            );
          } else {
            dispatch(deleteTask(TaskId(socketData.projectId, data.taskId)));
          }
          break;
        default:
      }
    },
    [
      dispatch,
      options?.onFetchTaskSuccess,
      options?.actorName,
      hopId,
      taskIdDebouncer,
      projectId,
      queryClient,
      options?.skipStoreUpdate,
    ],
  );

  useProjectWSUpdates(
    projectId,
    handleTaskSocketUpdate,
    EventType(Action.Update, ObjectType.Task),
  );

  useProjectWSUpdates(
    projectId,
    handleTaskSocketUpdate,
    EventType(Action.Create, ObjectType.Task),
  );

  useProjectWSUpdates(
    projectId,
    handleTaskSocketUpdate,
    EventType(Action.Delete, ObjectType.Task),
  );
  useProjectWSUpdates(
    projectId,
    handleTaskSocketUpdate,
    EventType(Action.Duplicate, ObjectType.Task),
  );
};
